/**
 * InstallPwaHint — captures the browser's `beforeinstallprompt`
 * event and surfaces a small "Install app" chip in the topbar.
 *
 * Why this exists: when the user runs the app in a normal browser
 * tab and the browser window is NOT maximised, content from other
 * windows behind the browser bleeds through on the right edge.
 * The InteriorIO app cannot paint outside its browser viewport
 * (sandbox restriction). The clean fix is to let the user
 * "Install" the app — Chrome / Edge / Brave then re-open it in a
 * standalone window without browser chrome OR neighbouring tabs,
 * which means no other window can ever bleed through.
 *
 * Visibility rules:
 *   1. Hidden when already running in standalone mode
 *      (`display-mode: standalone` media query matches OR the
 *      iOS `navigator.standalone` flag is set).
 *   2. Hidden when the browser has not fired
 *      `beforeinstallprompt` (Safari on macOS / Firefox never
 *      fire it; iOS Safari uses Add to Home Screen instead but
 *      doesn't surface programmatic installability).
 *   3. Hidden after the user clicks Dismiss (× icon) — flag
 *      persisted to localStorage so it doesn't reappear forever.
 *   4. Hidden after the user clicks Install (the OS install
 *      prompt opens; on success the next load is in standalone
 *      mode so rule #1 also kicks in).
 *
 * Renders as a small pill in the topbar — matches the existing
 * `.btn-ghost` style for visual consistency.
 */
import { useCallback, useEffect, useState } from 'react';

/** Local-storage key for the "user dismissed the hint" flag. */
const DISMISSED_KEY = 'iio.installHint.dismissed';

/** The Chromium `beforeinstallprompt` event interface (not in
 *  TS DOM lib because it's still an unofficial draft). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPwaHint() {
  const [promptEvent, setPromptEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  // Track standalone-mode separately so we re-render if the user
  // installs the app mid-session (some browsers fire the event +
  // a `display-mode` change without a page reload).
  const [standalone, setStandalone] = useState<boolean>(() =>
    isStandaloneMode(),
  );

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      // Stop the browser's mini-infobar from auto-showing — we
      // own the surface.
      e.preventDefault();
      setPromptEvent(e as BeforeInstallPromptEvent);
    }
    function onAppInstalled() {
      // Clear the event ref; rule #1 + #4 above kick in.
      setPromptEvent(null);
      setStandalone(true);
    }
    function onDisplayModeChange(e: MediaQueryListEvent) {
      if (e.matches) setStandalone(true);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener?.('change', onDisplayModeChange);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
      mq.removeEventListener?.('change', onDisplayModeChange);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!promptEvent) return;
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      // Whether accepted or dismissed, the event is one-shot — null it.
      setPromptEvent(null);
      if (choice.outcome === 'dismissed') {
        // User saw the OS dialog and said no. Don't nag.
        try {
          localStorage.setItem(DISMISSED_KEY, '1');
        } catch {
          /* private mode — ignore */
        }
        setDismissed(true);
      }
    } catch (e) {
      console.warn('[InstallPwaHint] prompt failed:', e);
    }
  }, [promptEvent]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* private mode — ignore */
    }
    setDismissed(true);
  }, []);

  // Hide when in standalone OR dismissed OR no install event captured.
  if (standalone) return null;
  if (dismissed) return null;
  if (!promptEvent) return null;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 6px 4px 10px',
        border: '1px solid var(--border)',
        borderRadius: 999,
        background: '#fff',
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--text-primary)',
        whiteSpace: 'nowrap',
      }}
      title="Install InteriorIO to your desktop — opens in its own window with no browser chrome"
    >
      <i
        className="ph-bold ph-download-simple"
        style={{ fontSize: 13, color: 'var(--brand)' }}
      />
      <button
        type="button"
        onClick={handleInstall}
        style={{
          border: 0,
          background: 'transparent',
          color: 'var(--text-primary)',
          font: 'inherit',
          fontWeight: 600,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        Install app
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss install hint"
        title="Don't show this again"
        style={{
          marginLeft: 2,
          border: 0,
          background: 'transparent',
          color: 'var(--text-tertiary)',
          cursor: 'pointer',
          padding: '2px 4px',
          display: 'inline-flex',
          borderRadius: 4,
        }}
      >
        <i className="ph ph-x" style={{ fontSize: 11 }} />
      </button>
    </div>
  );
}

// ───── helpers ─────

function isStandaloneMode(): boolean {
  // Chromium / desktop browsers expose the display-mode media query.
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
  ) {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  }
  // iOS Safari uses a non-standard navigator flag instead. Cast through
  // unknown because the field isn't in lib.dom.d.ts.
  const iosFlag = (navigator as unknown as { standalone?: boolean }).standalone;
  return iosFlag === true;
}
